import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { webChatService } from "../../service/web-chat.service";

interface WebChatMessage {
    id: number;
    conversationId: number;
    direction: "inbound" | "outbound";
    body: string;
    isActivio: boolean;
    sentAt: string;
}

interface ChatContextType {
    isOpen: boolean;
    sessionId: string | null;
    messages: WebChatMessage[];
    isLoading: boolean;
    openChat: () => void;
    closeChat: () => void;
    sendMessage: (text: string) => Promise<void>;
    updateVisitorInfo: (name?: string, email?: string, phone?: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
    children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<WebChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Initialize session from localStorage or create new
    useEffect(() => {
        const storedSessionId = localStorage.getItem("miactivo_chat_session");
        if (storedSessionId) {
            setSessionId(storedSessionId);
            // Load existing messages
            loadMessages(storedSessionId);
        }
    }, []);

    const loadMessages = async (sid: string) => {
        try {
            const result = await webChatService.getMessages(sid);
            setMessages(result.messages);
        } catch (err) {
            console.error("Failed to load messages:", err);
        }
    };

    const initSession = async () => {
        try {
            const result = await webChatService.initSession();
            setSessionId(result.sessionId);
            localStorage.setItem("miactivo_chat_session", result.sessionId);
            return result.sessionId;
        } catch (err) {
            console.error("Failed to initialize session:", err);
            throw err;
        }
    };

    const openChat = useCallback(async () => {
        setIsOpen(true);
        if (!sessionId) {
            await initSession();
        }
    }, [sessionId]);

    const closeChat = useCallback(() => {
        setIsOpen(false);
    }, []);

    const sendMessage = useCallback(
        async (text: string) => {
            if (!text.trim()) return;

            let currentSessionId = sessionId;

            // Create session if doesn't exist
            if (!currentSessionId) {
                currentSessionId = await initSession();
            }

            if (!currentSessionId) {
                throw new Error("Failed to create session");
            }

            setIsLoading(true);

            try {
                const result = await webChatService.sendMessage(
                    currentSessionId,
                    text,
                );

                // Add user message to local state
                setMessages((prev) => [
                    ...prev,
                    {
                        id: result.messageId,
                        conversationId: 0,
                        direction: "inbound",
                        body: text,
                        isActivio: false,
                        sentAt: new Date().toISOString(),
                    },
                ]);

                // Add AI response if present
                const aiResp = result.aiResponse;
                if (aiResp) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: aiResp.messageId,
                            conversationId: 0,
                            direction: "outbound",
                            body: aiResp.body,
                            isActivio: true,
                            sentAt: new Date().toISOString(),
                        },
                    ]);
                }
            } catch (err) {
                console.error("Failed to send message:", err);
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [sessionId],
    );

    const updateVisitorInfo = useCallback(
        async (name?: string, email?: string, phone?: string) => {
            if (!sessionId) return;

            try {
                await webChatService.updateVisitorInfo(sessionId, {
                    name,
                    email,
                    phone,
                });
            } catch (err) {
                console.error("Failed to update visitor info:", err);
                throw err;
            }
        },
        [sessionId],
    );

    return (
        <ChatContext.Provider
            value={{
                isOpen,
                sessionId,
                messages,
                isLoading,
                openChat,
                closeChat,
                sendMessage,
                updateVisitorInfo,
            }}
        >
            {children}
        </ChatContext.Provider>
    );
}

export function useChatContext(): ChatContextType {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChatContext must be used within a ChatProvider");
    }
    return context;
}
